export const AI_CHAPTER_TARGET_MIN = 1;
export const AI_CHAPTER_TARGET_MAX = 15;

export interface AiChapterPreset {
	value: string;
	lo: number;
	hi: number;
}

/**
 * 唯一合法预设集合；数组顺序即 UI 展示顺序（1-5 恒排在首位）。
 * UI 另有 Auto-Plan 预设（见 {@link AI_CHAPTER_AUTO_PLAN_PRESET}）：它就是一个普通区间值，
 * 走既有区间契约（lo=1 对任意有余额用户合法、预占 min(15, 余额)），不进本注册表、
 * 不参与默认链（aiChapterDefaultPreset 保持服务端空值分支语义不变）。
 */
export const AI_CHAPTER_PRESETS: readonly AiChapterPreset[] = [
	{ value: '1-5', lo: 1, hi: 5 },
	{ value: '6-8', lo: 6, hi: 8 },
	{ value: '9-12', lo: 9, hi: 12 },
	{ value: '13-15', lo: 13, hi: 15 },
];

export const AI_CHAPTER_DEFAULT_PRESET = '9-12';

/** Auto-Plan 预设：注入 1-15 全区间，管线在区间内自选实际章节数。 */
export const AI_CHAPTER_AUTO_PLAN_PRESET = '1-15';
export const AI_CHAPTER_AUTO_PLAN_LABEL = 'Auto-Plan';

/** UI 展示口径：Auto-Plan 显示品牌名，其余值原样返回（空值返回空串）。 */
export function aiChapterDisplayLabel(value: string | null | undefined): string {
	return value === AI_CHAPTER_AUTO_PLAN_PRESET ? AI_CHAPTER_AUTO_PLAN_LABEL : value ?? '';
}

/** 解析 `lo-hi` 区间并校验其落在合法边界内；非区间/越界返回 null。 */
export function aiChapterPresetBounds(value: string): { lo: number; hi: number } | null {
	const match = /^(\d+)-(\d+)$/.exec(value);
	if (!match) return null;
	const lo = Number(match[1]);
	const hi = Number(match[2]);
	return lo >= AI_CHAPTER_TARGET_MIN && hi <= AI_CHAPTER_TARGET_MAX && lo <= hi ? { lo, hi } : null;
}

/** 预设可选 ⟺ 余额 ≥ lo（1-5 恒可选：lo=1）。 */
export function isAiChapterPresetSelectable(value: string, availableCredits: number): boolean {
	const bounds = aiChapterPresetBounds(value);
	return bounds !== null && bounds.lo <= availableCredits;
}

/** 默认链：9-12 → 6-8 → 1-5（13-15 永不作为静默默认）。余额 0 返回 null（启动门控已拦截）。 */
export function aiChapterDefaultPreset(availableCredits: number): string | null {
	const available = Number.isFinite(availableCredits) ? Math.max(0, Math.floor(availableCredits)) : AI_CHAPTER_TARGET_MAX;
	for (const value of [AI_CHAPTER_DEFAULT_PRESET, '6-8', '1-5']) {
		if (isAiChapterPresetSelectable(value, available)) return value;
	}
	return null;
}
